/**
 * L_ENTITY (profile + directors + secretaries + members) → Singbac Incorporation Post fill values.
 *
 * Post-incorporation: 오퍼레이터가 이미 등록된 회사(ENTITY)를 고르면 회사·이사·비서·주주 정보가
 * 자동으로 채워진다. 공통 SG Incorporation (Post) preset(mapSgEntityToPostValues)과 동일한
 * 엔드포인트/응답 필드를 사용하되, Singbac 문서의 실제 변수 키(first_director_names /
 * sub{n}_member_* / dir_{id}/client_name / secretary_name …)로 매핑한다.
 * client_* 신원(주소·국적·DOB)·resident_director_*·witness 는 화면 ✨/수동 입력 대상(엔티티 아님).
 */
import { formatRegisteredOfficeAddressFromProfile } from '@/features/startTask/preset_task/sg/components/agm/agm-ufs/composables/agmUfsEntityAutofill';
import { dateToHtmlInput } from '@/features/startTask/preset_task/sg/components/change-financial-year-end/composables/dateToHtmlInput';

function str(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

function pick(...vals: unknown[]): string {
  for (const v of vals) {
    const s = str(v);
    if (s) return s;
  }
  return '';
}

/** CddVarField date 저장과 맞춤 (YYYY-MM-DD) — 화면은 세션 dateFormat 표시 */
function formatDocDate(v: unknown): string {
  return dateToHtmlInput(v) || str(v);
}

function unwrapProfile(raw: any): Record<string, any> {
  let p = raw?.data ?? raw ?? {};
  for (let i = 0; i < 2; i += 1) {
    const inner = p?.data;
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      if (
        inner.registeredAddress != null ||
        inner.regNo != null ||
        inner.REG_NO != null ||
        inner.accountingDates != null
      ) {
        p = inner;
        continue;
      }
    }
    break;
  }
  return p && typeof p === 'object' ? p : {};
}

function personName(row: any): string {
  return pick(
    row?.name,
    row?.contactName,
    row?.fullName,
    `${str(row?.firstName)} ${str(row?.surname || row?.lastName)}`.trim(),
  );
}

function personId(row: any): string {
  return pick(
    row?.contactRegNo,
    row?.CONTACT_REG_NO,
    row?.contact_reg_no,
    row?.idNo,
    row?.identificationNo,
    row?.IDENTIFICATION_NO,
    row?.nric,
    row?.nricNo,
    row?.passportNo,
    row?.ID_NO,
    row?.NRIC_NO,
    row?.PASSPORT_NO,
  );
}

function memberShares(row: any): string {
  return pick(row?.shares, row?.numberOfShares, row?.shareQty, row?.ordinaryShares, row?.qty);
}

function memberSharesQty(row: any): number {
  const n = Number(String(memberShares(row) || '0').replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : 0;
}

export interface SingbacEntityMapped {
  values: Record<string, string>;
  local: {
    directorIds: number[];
    /** L_OFFICER.IS_NOMINEE=1 인 이사가 있으면 true — Notice by Nominee / ND Service & Indemnity 생성 여부 판단 */
    hasNominee: boolean;
    /** 노미니 디렉터(IS_NOMINEE=1) 이름 (resident director 시드용) */
    nomineeName: string;
  };
}

/** L_OFFICER.IS_NOMINEE=1 판정 — snake/camel 및 0/1·boolean·'Y' 대응 */
function isNomineeRow(row: any): boolean {
  const v = row?.isNominee ?? row?.IS_NOMINEE ?? row?.is_nominee;
  if (v === undefined || v === null || v === '') return false;
  return v === true || v === 1 || v === '1' || String(v).toUpperCase() === 'Y';
}

/**
 * L_ENTITY snapshot → Singbac Post 문서 필드 값 + per-director 부수(local).
 * 반환된 values 는 패널에서 "빈 칸만" 시드한다(사용자 입력 미덮어씀).
 */
export function mapSingbacEntityToPostValues(input: {
  profile?: any;
  entityHint?: Record<string, any> | null;
  directors?: any[];
  secretaries?: any[];
  members?: any[];
}): SingbacEntityMapped {
  const values: Record<string, string> = {};
  const set = (k: string, v: unknown) => {
    const s = str(v);
    if (s) values[k] = s;
  };

  const profile = unwrapProfile(input.profile);
  const hint = input.entityHint || {};
  const accounting = (profile.accountingDates as Record<string, any>) || {};

  const companyName = pick(
    hint.companyName,
    hint.entityName,
    profile.companyName,
    profile.entityName,
    profile.COMPANY_NAME,
  );
  const regNo = pick(
    hint.registrationNo,
    hint.regNo,
    hint.uen,
    hint.entityUen,
    profile.regNo,
    profile.REG_NO,
    profile.registrationNo,
    profile.uen,
    profile.UEN,
  );
  const incorpDate = formatDocDate(
    pick(
      hint.incorporationDate,
      hint.dateOfIncorporation,
      accounting.incorporationDate,
      profile.incorporationDate,
      profile.INCORPORATION_DATE,
      profile.dateOfIncorporation,
      profile.DATE_OF_INCORPORATION,
    ),
  );
  const office = pick(
    hint.registeredAddress,
    hint.registeredOfficeAddress,
    formatRegisteredOfficeAddressFromProfile(profile),
  );
  const fyeRaw = pick(
    accounting.financialYearEnd,
    profile.financialYearEnd,
    profile.FINANCIAL_YEAR_END,
  );
  const fye = formatDocDate(fyeRaw) || fyeRaw;

  set('company_name', companyName);
  set('company_registration_no', regNo);
  set('registered_office_address', office);
  set('incorporation_date', incorpDate);
  set('financial_year_end', fye);

  const directors = (input.directors || []).filter((d) => personName(d));
  const secretaries = (input.secretaries || []).filter((s) => personName(s));
  // 해임/잔여주식 0 주주 제외
  const members = (input.members || []).filter((m) => personName(m) && memberSharesQty(m) > 0);

  const firstSec = secretaries[0];
  if (firstSec) set('secretary_name', personName(firstSec));

  // 노미니 디렉터 판정(L_OFFICER.IS_NOMINEE=1) — 노미니가 있어야 Notice by Nominee / ND Service & Indemnity 생성.
  //  최상위 client_*(Notice nominator·Undertaking·Indemnity·Chart 의 Client) 는 controller 레코드가 아니라
  //  노미니 이사의 NOMINEE_DIRCTOR_CD 가 가리키는 contact(전체 신원)로 채운다(패널에서 처리).
  const nomineeDir = (input.directors || []).find((d) => isNomineeRow(d));
  const hasNominee = !!nomineeDir;
  const nomineeName = nomineeDir ? personName(nomineeDir) : '';
  // resident_director_* 는 패널에서 노미니별 nom_{id}/ 스코프로 채운다(여기서 top-level set 안 함).

  // DRIW — "the persons named as first Directors" (쉼표 결합)
  if (directors.length) set('first_director_names', directors.map(personName).join(', '));

  // NON NOMINEE / Disclosure per client-director 부수 (DocScope dir_{id}/)
  const directorIds: number[] = [];
  directors.forEach((d, i) => {
    const id = i + 1;
    directorIds.push(id);
    // NON NOMINEE / Disclosure 부수 — 각 client-director 를 client_name(dir 스코프)으로.
    set(`dir_${id}/client_name`, personName(d));
  });
  if (!directorIds.length) directorIds.push(1);

  // DRIW — Registration of Subscribers (Entity 멤버, 잔여주식>0)
  members.forEach((m, i) => {
    const id = i + 1;
    set(`sub${id}_member_name`, personName(m));
    set(`sub${id}_member_shares`, memberShares(m));
  });

  return { values, local: { directorIds, hasNominee, nomineeName } };
}
